import type {
	Field,
	FileValue,
} from "@CommonLib/src/frontend/WizardForm/types";
import {
	getChoicesFromField,
	getFieldSource,
} from "@CommonLib/src/frontend/WizardForm/utils";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, type ControllerRenderProps } from "react-hook-form";
import { match } from "ts-pattern";

// Separate component for file input to properly manage state
const FileInput = ({
	fieldKey,
	controllerField,
	errors,
	onKeystroke,
	onFileUpload,
}: {
	fieldKey: string;
	// biome-ignore lint/suspicious/noExplicitAny: expected here
	controllerField: ControllerRenderProps<any, string>;
	// biome-ignore lint/suspicious/noExplicitAny: expected here
	errors: Record<string, any>;
	onKeystroke: (key: string) => void;
	onFileUpload?: ((fieldKey: string) => void) | undefined;
}) => {
	const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MiB
	const [error, setError] = useState<string | null>(null);
	const [fileName, setFileName] = useState<string | null>(null);

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];

		if (!file) {
			controllerField.onChange(null);
			setFileName(null);
			setError(null);
			return;
		}

		// Validate file type
		if (file.type !== "application/pdf") {
			setError("Only PDF files are allowed");
			controllerField.onChange(null);
			setFileName(null);
			event.target.value = "";
			return;
		}

		// Validate file size
		if (file.size > MAX_FILE_SIZE_BYTES) {
			setError(
				`File size must be less than ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB`,
			);
			controllerField.onChange(null);
			setFileName(null);
			event.target.value = "";
			return;
		}

		// Convert to base64
		try {
			const base64Data = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});

			const fileValue: FileValue = {
				filename: file.name,
				base64Data,
				size: file.size,
			};

			controllerField.onChange(fileValue);
			setFileName(file.name);
			setError(null);
			// Track keystroke (file selection time)
			onKeystroke(fieldKey);
			onFileUpload?.(fieldKey);
		} catch {
			setError("Error reading file");
			controllerField.onChange(null);
			setFileName(null);
			event.target.value = "";
		}
	};

	const handleRemoveFile = () => {
		controllerField.onChange(null);
		setFileName(null);
		setError(null);
		// Reset the input
		const input = document.querySelector(
			`input[data-field-key="${fieldKey}"]`,
		) as HTMLInputElement;
		if (input) {
			input.value = "";
		}
	};

	return (
		<Box>
			<input
				type="file"
				accept=".pdf,application/pdf"
				data-field-key={fieldKey}
				onChange={handleFileChange}
				style={{ display: "block", marginBottom: "8px" }}
			/>
			<Typography variant="caption" color="text.secondary">
				(PDF only, max 5MB)
			</Typography>
			{fileName && (
				<Box mt={1} display="flex" alignItems="center" gap={1}>
					<Typography variant="body2" color="text.primary">
						Selected: {fileName}
					</Typography>
					<Button size="small" onClick={handleRemoveFile}>
						Remove
					</Button>
				</Box>
			)}
			{error && (
				<Typography variant="caption" color="error" display="block" mt={0.5}>
					{error}
				</Typography>
			)}
			{errors[fieldKey] && (
				<Typography variant="caption" color="error" display="block" mt={0.5}>
					{errors[fieldKey]?.message}
				</Typography>
			)}
		</Box>
	);
};

export const renderField = (
	stepId: number,
	field: Field,
	onKeystroke: (key: string) => void,
	// biome-ignore lint/suspicious/noExplicitAny: expected here
	control: any,
	// biome-ignore lint/suspicious/noExplicitAny: expected here
	errors: Record<string, any>,
	onFileUpload?: ((fieldKey: string) => void) | undefined,
	onSelectChoice?: ((fieldKey: string) => void) | undefined,
) => {
	const fieldKey = getFieldSource(stepId, field.identifier);
	const isRequired = !!field.required;

	return (
		<Box key={field.id} mb={2}>
			<Typography
				variant="body2"
				color="text.primary"
				sx={{ mb: 0.5, whiteSpace: "pre-line" }}
			>
				<b>{field.description}</b>
			</Typography>

			<Controller
				name={fieldKey}
				control={control}
				rules={isRequired ? { required: "Required field" } : {}}
				render={({ field: controllerField }) =>
					match(field.type)
						.with("text", () => (
							<TextField
								{...controllerField}
								fullWidth
								error={!!errors[fieldKey]}
								helperText={errors[fieldKey]?.message}
								onChange={(e) => {
									controllerField.onChange(e);
									onKeystroke(fieldKey);
								}}
							/>
						))
						.with("select", () => (
							<TextField
								{...controllerField}
								select
								fullWidth
								error={!!errors[fieldKey]}
								helperText={errors[fieldKey]?.message}
								onChange={(e) => {
									controllerField.onChange(e);
									onKeystroke(fieldKey);
									onSelectChoice?.(fieldKey);
								}}
							>
								{getChoicesFromField(field).map((choice) => (
									<MenuItem key={choice.id} value={choice.id}>
										{choice.name}
									</MenuItem>
								))}
							</TextField>
						))
						.with("long_text", () => (
							<TextField
								{...controllerField}
								fullWidth
								multiline
								minRows={4}
								error={!!errors[fieldKey]}
								helperText={errors[fieldKey]?.message}
								onChange={(e) => {
									controllerField.onChange(e);
									onKeystroke(fieldKey);
								}}
							/>
						))
						.with("file", () => (
							<FileInput
								fieldKey={fieldKey}
								controllerField={controllerField}
								errors={errors}
								onKeystroke={onKeystroke}
								onFileUpload={onFileUpload}
							/>
						))
						.exhaustive()
				}
			/>
		</Box>
	);
};

export const renderFieldView = (
	fieldIdentifier: number,
	fieldType: Field["type"],
	_fieldLabel: string,
	fieldDescription: string,
	fieldValue: string | number | { filename: string; size: number },
) => {
	return (
		<Box key={fieldIdentifier} mb={2}>
			<Typography
				variant="body2"
				color="text.primary"
				sx={{ mb: 0.5, whiteSpace: "pre-line" }}
			>
				{fieldDescription}
			</Typography>
			{match(fieldType)
				.with("text", () => (
					<Typography>
						<b>{String(fieldValue)}</b>
					</Typography>
				))
				.with("select", () => (
					<Typography>
						<b>{String(fieldValue)}</b>
					</Typography>
				))
				.with("long_text", () => (
					<Typography sx={{ whiteSpace: "pre-line" }}>
						<b>{String(fieldValue)}</b>
					</Typography>
				))
				.with("file", () => {
					if (typeof fieldValue === "object" && fieldValue !== null) {
						const sizeInMB = (fieldValue.size / (1024 * 1024)).toFixed(2);
						return (
							<Typography>
								{fieldValue.filename} ({sizeInMB} MB)
							</Typography>
						);
					}
					if (typeof fieldValue === "string") {
						// If fieldValue is a string, it could be:
						// 1. A presigned URL (starts with https://)
						// 2. An error message like "Unable to load file"
						// 3. Empty string or no file uploaded
						if (
							fieldValue.startsWith("https://") ||
							fieldValue.startsWith("http://")
						) {
							// Extract filename from the URL or use a generic name
							const urlParts = fieldValue.split("/");
							const filenamePart = urlParts[urlParts.length - 1]?.split("?")[0];
							const filename = filenamePart
								? decodeURIComponent(filenamePart)
								: "View file";
							return (
								<Typography>
									<a
										href={fieldValue}
										target="_blank"
										rel="noopener noreferrer"
									>
										{filename}
									</a>
								</Typography>
							);
						}
						if (fieldValue === "Unable to load file") {
							return <Typography color="error">{fieldValue}</Typography>;
						}
					}
					return <Typography>No file uploaded</Typography>;
				})
				.exhaustive()}
		</Box>
	);
};
